import { ORDER_FIELD_LABELS, formatSequelizeLine } from './fieldLabels.js';

// Parses the backend's GlobalError response shape: { name, message, code, details? }
// Only two cases are safe to surface to the user in detail: the
// InvalidRequestBodyError case (code === 'INVALID_REQUEST_BODY_ERROR') and the
// Sequelize validation-error case (code === 'SEQUELIZE_VALIDATION_ERROR').
// `details` is backend-controlled; exactly which shape it's in decides what we show:
//   - a string               -> shown as-is
//   - an array of strings    -> shown as a bullet list
//   - an array of objects    -> each item's `userFriendlyMessage` for invalid
//                              request body errors, or `message` for Sequelize
//                              validation errors (dropping any that don't have
//                              one); if none do, the generic message
//   - an object               -> its `userFriendlyMessage`, else the generic message
//   - absent                  -> generic "invalid data" message (the raw backend
//                              `message` isn't assumed to be user-facing text)
// Sequelize detail paths are renamed to user-facing labels via the passed
// labels map (default ORDER_FIELD_LABELS); the label is prepended to the
// message when it doesn't already mention the field.
// Any error that ISN'T one of those (network failure, 500, unrecognized shape)
// gets the separate generic retry message instead.
const GENERIC_FAILURE_MESSAGE = 'Something went wrong. Please try again after some time.';
const INVALID_DATA_MESSAGE = 'Invalid data provided';
const INVALID_REQUEST_BODY_CODE = 'INVALID_REQUEST_BODY_ERROR';
const SEQUELIZE_VALIDATION_CODE = 'SEQUELIZE_VALIDATION_ERROR';

export function parseOrderApiError(err, labels = ORDER_FIELD_LABELS) {
  const data = err?.response?.data;
  const isSequelizeError = data?.code === SEQUELIZE_VALIDATION_CODE;
  const isInvalidRequestBody =
    !isSequelizeError &&
    (data?.code === INVALID_REQUEST_BODY_CODE);

  if (!isInvalidRequestBody && !isSequelizeError) {
    return { title: GENERIC_FAILURE_MESSAGE, lines: [] };
  }

  if (isSequelizeError) {
    const { details } = data;
    const lines = [];

    if (typeof details === 'string') {
      if (details) lines.push(details);
    } else if (Array.isArray(details)) {
      details.forEach((item) => {
        const msg = typeof item === 'string' ? item : formatSequelizeLine(item, labels);
        if (msg) lines.push(msg);
      });
    }

    return lines.length ? { title: null, lines } : { title: INVALID_DATA_MESSAGE, lines: [] };
  }

  const { details } = data;

  if (details == null) {
    return { title: INVALID_DATA_MESSAGE, lines: [] };
  }

  if (typeof details === 'string') {
    return { title: details, lines: [] };
  }

  if (Array.isArray(details)) {
    if (details.every((item) => typeof item === 'string')) {
      return details.length
        ? { title: null, lines: details }
        : { title: INVALID_DATA_MESSAGE, lines: [] };
    }

    const messages = details
      .map((item) => (item && typeof item === 'object' ? item.userFriendlyMessage : null))
      .filter(Boolean);

    return messages.length
      ? { title: null, lines: messages }
      : { title: INVALID_DATA_MESSAGE, lines: [] };
  }

  if (typeof details === 'object') {
    return { title: details.userFriendlyMessage || INVALID_DATA_MESSAGE, lines: [] };
  }

  return { title: INVALID_DATA_MESSAGE, lines: [] };
}

// General-purpose classifier used by every read/fetch call site (useRetryFetch,
// one-off dropdown fetches, etc.) — NOT for order-submit errors, which stay on
// parseOrderApiError above. Buckets an axios error into one of:
//   - entity not found (GlobalError code ENTITY_NOT_FOUND_ERROR): show the
//     backend's own message, since the resource genuinely doesn't exist.
//   - unavailable (plain 404, or no response at all — server down, refused
//     connection, DNS/timeout): a generic "service unavailable" message.
//   - anything else (400/401/403/500/validation/etc.): returns message: null
//     so the caller falls back to its own existing default text.
const SERVER_UNAVAILABLE_MESSAGE = 'The server is currently unavailable. Please try again later.';
const ENTITY_NOT_FOUND_CODE = 'ENTITY_NOT_FOUND_ERROR';
const NETWORK_ERROR_CODES = new Set([
  'ECONNREFUSED',
  'ERR_NETWORK',
  'ECONNABORTED',
  'ENOTFOUND',
  'ETIMEDOUT',
]);

export function classifyPortalError(err) {
  const data = err?.response?.data;

  if (data?.code === ENTITY_NOT_FOUND_CODE) {
    return {
      isEntityNotFound: true,
      isUnavailable: false,
      message: data?.message || 'The requested item could not be found.',
    };
  }

  const isNetworkDown =
    !err?.response || NETWORK_ERROR_CODES.has(err?.code) || err?.message === 'Network Error';

  if (isNetworkDown || err?.response?.status === 404) {
    return { isEntityNotFound: false, isUnavailable: true, message: SERVER_UNAVAILABLE_MESSAGE };
  }

  return { isEntityNotFound: false, isUnavailable: false, message: null };
}
